import { ThemeOptions, useTheme } from '@mui/material';
import MuiTooltip, {
    tooltipClasses as muiTooltipClasses,
    TooltipProps as MuiTooltipProps
} from '@mui/material/Tooltip';
import * as React from 'react';

import { useClickAway, useMounted } from '../hooks';
import { Markdown } from '../markdown';
import { withVersion } from '../utils';

export interface TooltipProps
    extends TypedOmit<
        MuiTooltipProps,
        // These props are not allowed to be overridden as they are used internally.
        'onMouseEnter' | 'onMouseLeave' | 'onFocus' | 'onBlur' | 'ref'
    > {
    /** prevent display of the tooltip, while still rendering it as a component */
    disabled?: boolean;
    /** prevent rendering of the tooltip altogether */
    bypass?: boolean;
    /** @default default */
    mode?: 'default' | 'light';
    onVisibilityChange?(visible: boolean): void;
}

const preventEmailLinks = (text: string) =>
    text.replace(/<([^>]+@[^>]+\.[^>]+)>/g, '\\<$1>');

const replaceNewlineWithBr = (text: string) => text.replace(/\n/g, '<br>');

function getTooltipTitle(tooltip: TooltipProps['title']) {
    if (typeof tooltip === 'string') {
        return (
            <Markdown>
                {preventEmailLinks(replaceNewlineWithBr(tooltip))}
            </Markdown>
        );
    }

    return tooltip;
}

function useDisabled(
    disabled: boolean,
    popperRef: React.RefObject<HTMLDivElement>
) {
    const [internalDisabled, setInternalDisabled] = React.useState(false);
    const mounted = useMounted();
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    const onMouseEnter = () => {
        clearTimeout(timeoutRef.current!);
        setInternalDisabled(false);
    };

    /**
     * This is a workaround disabling the tooltip when the mouse leaves the tooltip.
     */
    const onMouseLeave = () => {
        const popper = popperRef.current;
        let targetIsPopper = false;

        const onMouseMove = (e: MouseEvent) => {
            if (e.target === popper || popper?.contains(e.target as Node)) {
                targetIsPopper = true;
            }
        };

        popper?.addEventListener('mousemove', onMouseMove);

        timeoutRef.current = setTimeout(() => {
            popper?.removeEventListener('mousemove', onMouseMove);

            if (!targetIsPopper && mounted.current) {
                setInternalDisabled(true);
            }
        }, 150);
    };

    const onClick = () => {
        setInternalDisabled(true);
    };

    React.useEffect(() => {
        return () => {
            clearTimeout(timeoutRef.current!);
        };
    }, []);

    return {
        disabled: internalDisabled || disabled,
        tooltipProps: {
            onMouseEnter,
            onMouseLeave,
            onClick,
            onFocus: onMouseEnter,
            onBlur: onMouseLeave
        } satisfies Partial<MuiTooltipProps>
    };
}

export const TooltipImpl = React.forwardRef<HTMLElement, TooltipProps>(
    (props, ref) => {
        const { palette, shadows } = useTheme();
        const [open, setOpen] = React.useState(props.open ?? false);
        const popperRef = React.useRef<HTMLDivElement>(null);
        const {
            onVisibilityChange,
            mode,
            disabled: _disabled,
            bypass = false,
            slotProps,
            ...muiProps
        } = props;
        const { tooltipProps, disabled } = useDisabled(
            _disabled ?? false,
            popperRef
        );

        const onClose = () => {
            setOpen(false);
            onVisibilityChange?.(false);
        };

        const onOpen = () => {
            setOpen(true);
            onVisibilityChange?.(true);
        };

        const titleProps = useClickAway<HTMLDivElement>(open, onClose);

        const effectiveDisabled = disabled || bypass;

        const title =
            effectiveDisabled ||
            props.title === null ||
            props.title === undefined ? null : (
                <div {...titleProps}>{getTooltipTitle(props.title)}</div>
            );

        return (
            <MuiTooltip
                open={open}
                {...muiProps}
                {...tooltipProps}
                ref={ref}
                placement={props.placement}
                onOpen={onOpen}
                onClose={onClose}
                data-role="mui-tooltip"
                slotProps={{
                    transition: effectiveDisabled
                        ? {
                              timeout: 0
                          }
                        : undefined,
                    popper: {
                        ref: popperRef,
                        sx:
                            mode === 'light'
                                ? // @see https://mui.com/material-ui/react-tooltip/#customization
                                  {
                                      [`& .${muiTooltipClasses.tooltip}`]: {
                                          backgroundColor:
                                              palette.background.paper,
                                          color: palette.text.primary,
                                          boxShadow: shadows[1]
                                      }
                                  }
                                : {},
                        modifiers: [
                            // @see https://mui.com/material-ui/react-tooltip/#distance-from-anchor
                            {
                                name: 'offset',
                                options: {
                                    offset: [0, -9]
                                }
                            }
                        ]
                    },
                    ...slotProps
                }}
                title={title}
            />
        );
    }
);

export const Tooltip = withVersion(TooltipImpl, '1.0.0');

Tooltip.displayName = 'Tooltip';

export const tooltipStyle = {
    MuiTooltip: {
        styleOverrides: {
            popper: {
                pointerEvents: 'none',

                '.MuiTooltip-tooltip': {
                    pointerEvents: 'auto'
                }
            }
        }
    }
} satisfies ThemeOptions['components'];
